import React, { useEffect, useState } from "react";

import axios from "axios";

import Navbar from "../components/Navbar";

import {
  CalendarDays,
  Clock3,
  User,
  Phone,
  CircleCheckBig,
  CircleX,
  CircleDashed,
  FlaskConical,
  TestTube2,
  Home,
  Building2,
} from "lucide-react";
import {API_URL,FAST_API_URL} from "../api"

const PatientLabBookings = () => {

  const [bookings, setBookings] =
    useState([]);

  const [loading, setLoading] =
    useState(true);

  // =====================================================
  // FETCH BOOKINGS
  // =====================================================

  const fetchBookings = async () => {

    try {

      const token =
        localStorage.getItem(
          "token"
        );

      const response =
        await axios.post(
          `${API_URL}/patient-all-lab-bookings`,
          {},
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

      setBookings(
        response?.data
          ?.bookings || []
      );

    } catch (error) {

      console.log(error);

    } finally {

      setLoading(false);

    }

  };

  useEffect(() => {

    fetchBookings();

  }, []);

  // =====================================================
  // STATUS MAP
  // =====================================================

  const statusMap = {

    1: "proposed",

    2: "confirmed",

    3: "collected",

    4: "cancelled",

    5: "missed",

    6: "received in lab",

    7: "results shared",

  };

  // =====================================================
  // STATUS UI
  // =====================================================

  const getStatusUI = (
    status
  ) => {

    switch (status) {

      case "confirmed":

        return {

          color:
            "bg-green-100 text-green-700 border-green-200",

          icon: (
            <CircleCheckBig className="w-4 h-4" />
          ),
        };

      case "cancelled":

        return {

          color:
            "bg-red-100 text-red-700 border-red-200",

          icon: (
            <CircleX className="w-4 h-4" />
          ),
        };

      case "proposed":

        return {

          color:
            "bg-yellow-100 text-yellow-700 border-yellow-200",

          icon: (
            <CircleDashed className="w-4 h-4" />
          ),
        };

      case "results shared":

        return {

          color:
            "bg-blue-100 text-blue-700 border-blue-200",

          icon: (
            <CircleCheckBig className="w-4 h-4" />
          ),
        };

      default:

        return {

          color:
            "bg-gray-100 text-gray-700 border-gray-200",

          icon: (
            <CircleDashed className="w-4 h-4" />
          ),
        };
    }
  };

  // =====================================================
  // LOADING
  // =====================================================

  if (loading) {

    return (

      <>
        <Navbar />

        <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-cyan-50 pt-24 p-4">

          <div className="max-w-6xl mx-auto">

            <div className="animate-pulse grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">

              {[...Array(6)].map(
                (_, index) => (

                  <div
                    key={index}
                    className="bg-white rounded-3xl h-80 shadow-sm"
                  />

                )
              )}

            </div>

          </div>

        </div>

      </>
    );
  }

  return (

    <>
      {/* ================================================= */}
      {/* NAVBAR */}
      {/* ================================================= */}

      <Navbar />

      {/* ================================================= */}
      {/* PAGE */}
      {/* ================================================= */}

      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-cyan-50 pt-24 pb-10 px-4">

        {/* HEADER */}

        <div className="max-w-7xl mx-auto mb-8">

          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">

            <div>

              <h1 className="text-3xl font-bold text-gray-800">
                My Lab Bookings
              </h1>

              <p className="text-gray-500 mt-2">
                Track all your lab bookings easily
              </p>

            </div>

            <div className="bg-white px-5 py-3 rounded-2xl shadow-sm border border-indigo-100">

              <p className="text-sm text-gray-500">
                Total Bookings
              </p>

              <h2 className="text-2xl font-bold text-indigo-600">
                {bookings.length}
              </h2>

            </div>

          </div>

        </div>

        {/* EMPTY */}

        {bookings.length === 0 ? (

          <div className="max-w-xl mx-auto bg-white rounded-3xl shadow-lg p-10 text-center">

            <div className="bg-indigo-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto">

              <FlaskConical className="w-10 h-10 text-indigo-600" />

            </div>

            <h2 className="text-2xl font-bold text-gray-800 mt-5">
              No Lab Bookings Found
            </h2>

            <p className="text-gray-500 mt-2">
              You don’t have any lab bookings yet.
            </p>

          </div>

        ) : (

          <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">

            {bookings.map(
              (item, index) => {

                const statusLabel =
                  statusMap[
                    Number(
                      item?.booking_status
                        ?.status_code
                    )
                  ] || "unknown";

                const statusUI =
                  getStatusUI(
                    statusLabel
                  );

                return (

                  <div
                    key={index}
                    className="
                      relative
                      bg-white/90
                      backdrop-blur-xl
                      rounded-3xl
                      shadow-lg
                      border border-white
                      overflow-hidden
                      hover:shadow-2xl
                      transition-all duration-300
                      hover:-translate-y-1
                    "
                  >

                    {/* TOP */}

                    <div className="h-2 bg-gradient-to-r from-indigo-500 via-blue-500 to-cyan-400" />

                    {/* BODY */}

                    <div className="p-6">

                      {/* STATUS */}

                      <div className="flex justify-between items-start mb-5">

                        <div>

                          <h2 className="text-lg font-bold text-gray-800">
                            {
                              item?.lab
                                ?.lab_name
                            }
                          </h2>

                          <p className="text-sm text-gray-500 mt-1">
                            Booking ID:
                            #
                            {
                              item?.booking_id
                            }
                          </p>

                        </div>

                        <div
                          className={`
                            flex items-center gap-1
                            px-3 py-1.5
                            rounded-full
                            border
                            text-xs font-semibold capitalize
                            ${statusUI.color}
                          `}
                        >

                          {statusUI.icon}

                          {
                            statusLabel
                          }

                        </div>

                      </div>

                      {/* INFO */}

                      <div className="space-y-4">

                        {/* LAB */}

                        <div className="flex items-center gap-3">

                          <div className="bg-indigo-100 p-2 rounded-xl">

                            <FlaskConical className="w-5 h-5 text-indigo-600" />

                          </div>

                          <div>

                            <p className="text-xs text-gray-500">
                              Lab
                            </p>

                            <p className="text-sm font-semibold text-gray-800">
                              {
                                item?.lab
                                  ?.lab_name
                              }
                            </p>

                          </div>

                        </div>

                        {/* PHONE */}

                        <div className="flex items-center gap-3">

                          <div className="bg-green-100 p-2 rounded-xl">

                            <Phone className="w-5 h-5 text-green-600" />

                          </div>

                          <div>

                            <p className="text-xs text-gray-500">
                              Phone
                            </p>

                            <p className="text-sm font-semibold text-gray-800">
                              {
                                item?.lab
                                  ?.lab_phone
                              }
                            </p>

                          </div>

                        </div>

                        {/* DATE */}

                        <div className="flex items-center gap-3">

                          <div className="bg-purple-100 p-2 rounded-xl">

                            <CalendarDays className="w-5 h-5 text-purple-600" />

                          </div>

                          <div>

                            <p className="text-xs text-gray-500">
                              Booking Date
                            </p>

                            <p className="text-sm font-semibold text-gray-800">
                              {
                                item?.booking_status
                                  ?.event_date
                              }
                            </p>

                          </div>

                        </div>

                        {/* TIME */}

                        <div className="flex items-center gap-3">

                          <div className="bg-orange-100 p-2 rounded-xl">

                            <Clock3 className="w-5 h-5 text-orange-600" />

                          </div>

                          <div>

                            <p className="text-xs text-gray-500">
                              Booking Time
                            </p>

                            <p className="text-sm font-semibold text-gray-800">
                              {
                                item?.booking_status
                                  ?.event_time
                              }
                            </p>

                          </div>

                        </div>

                        {/* TEST TYPE */}

                        <div className="flex items-center gap-3">

                          <div className="bg-cyan-100 p-2 rounded-xl">

                            {item?.test_type ===
                            "At home" ? (

                              <Home className="w-5 h-5 text-cyan-600" />

                            ) : (

                              <Building2 className="w-5 h-5 text-cyan-600" />

                            )}

                          </div>

                          <div>

                            <p className="text-xs text-gray-500">
                              Test Type
                            </p>

                            <p className="text-sm font-semibold text-gray-800">
                              {
                                item?.test_type
                              }
                            </p>

                          </div>

                        </div>

                        {/* TESTS */}

                        {item?.test_names
                          ?.length > 0 && (

                          <div>

                            <div className="flex items-center gap-2 mb-3">

                              <TestTube2 className="w-5 h-5 text-indigo-600" />

                              <h3 className="font-semibold text-gray-800">
                                Selected Tests
                              </h3>

                            </div>

                            <div className="flex flex-wrap gap-2">

                              {item.test_names.map(
                                (
                                  test,
                                  idx
                                ) => (

                                  <div
                                    key={idx}
                                    className="
                                      px-3
                                      py-2
                                      rounded-xl
                                      bg-indigo-50
                                      border
                                      border-indigo-200
                                      text-indigo-700
                                      text-sm
                                      font-semibold
                                    "
                                  >
                                    {test}
                                  </div>

                                )
                              )}

                            </div>

                          </div>

                        )}

                      </div>

                      {/* FOOTER */}

                      <div className="mt-6 pt-4 border-t border-gray-100 flex justify-between items-center">

                        <p className="text-xs text-gray-400">
                          Created:
                        </p>

                        <p className="text-xs font-medium text-gray-600">
                          {new Date(
                            item?.created_at
                          ).toLocaleString()}
                        </p>

                      </div>

                    </div>

                  </div>

                );

              }
            )}

          </div>

        )}

      </div>

    </>
  );
};

export default PatientLabBookings;